import type {
  PremiumAchPull,
  PremiumAchPullStatus,
  QueryFilter,
  Location,
  Pagination
} from '../types'
import type { TableColumnsType } from 'antd'

import { CaretDownOutlined, CloseOutlined, SearchOutlined, SyncOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { Dropdown, Input, Space, Table, Tag } from 'antd'
import cx from 'classnames'
import { format, parseISO } from 'date-fns'
import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import $api from '../../../API'
import { CellTitle } from '../components/cell-title'
import { EmptyCellValue } from '../components/empty-cell-value'
import { currency, duration } from '../formatters'
import { baseCellClassName, baseHeaderCellClassName } from '../groups'
import { PremiumAchPullStatusMapper } from '../types'

type QueryContextKey = [
  'batches',
  // ListState['sort'],
  Pagination
  // ListState['searchParams']
]

interface QueryData {
  groups: PremiumAchPull[]
  totalFiltered: number
  totalUnfiltered: number
  filters: QueryDataFilters
}

interface QueryDataFilters {
  premium_funding_type: QueryFilter[]
  premium_schedule_day: QueryFilter[]
  premium_is_active: QueryFilter[]
  open_ar: QueryFilter[]
  current_ar: QueryFilter[]
  location: QueryFilter[]
  flags: QueryFilter[]
  next_pull_date: QueryFilter[]
}

export const PremiumAchPullsPage = (): JSX.Element => {
  const navigate = useNavigate()

  const [pagination, setPagination] = useState<Pagination>({ pageSize: 10, currentPage: 0 })

  const columns = useMemo<TableColumnsType<PremiumAchPull>>(
    () => [
      {
        title({ sortColumns }) {
          return (
            <CellTitle id='name' sortColumns={sortColumns}>
              Name
            </CellTitle>
          )
        },
        key: 'name',
        dataIndex: 'name',
        sorter: true,
        showSorterTooltip: false,
        // width: '15%'
        onCell: () => ({ className: cx(baseCellClassName) }),
        onHeaderCell: () => ({ className: cx(baseHeaderCellClassName) })
        // onCell (data, index) {
        //   return {
        //     className: cx(baseCellClassName)
        //   }
        // },
        // onHeaderCell (data, index) {
        //   return {
        //     className: cx(baseHeaderCellClassName)
        //   }
        // }
      },
      {
        title: 'Location',
        key: 'location',
        dataIndex: 'location',
        // width: '7%',
        // align: 'center',

        // filters: locationsQuery.data
        //   ?.map(location => {
        //     let filter = query.data?.filters.location.find(filter => filter.value === location.id) ?? { count: 0, value: location.id }

        //     return {
        //       ...filter,
        //       name: location.name,
        //       short_name: location.short_name
        //     }
        //   })
        //   .map(filter => ({
        //     text: (
        //       <div
        //         className={cx(
        //           'flex justify-between items-center gap-6',
        //           (filter.value === null || filter.count === 0) && 'text-gray-400'
        //         )}
        //       >
        //         <span>
        //           {filter.name}
        //           {' '}
        //           <span className='text-gray-500'>
        //             ({filter.short_name})
        //           </span>
        //         </span>

        //         <span>
        //           {filter.count}
        //         </span>
        //       </div>
        //     ),
        //     value: filter.value as string
        //   })),

        filterMultiple: true,
        // filteredValue: state.searchParams.locationIds,

        onCell: () => ({ className: cx(baseCellClassName) }),
        onHeaderCell: () => ({ className: cx(baseHeaderCellClassName) }),
        render(value: Location) {
          return value.short_name
        }
      },
      {
        title({ sortColumns }) {
          return (
            <CellTitle id='sending_date' sortColumns={sortColumns}>
              Scheduled at
            </CellTitle>
          )
        },
        key: 'sending_date',
        dataIndex: 'sending_date',
        sorter: true,
        showSorterTooltip: false,
        onCell: () => ({ className: cx(baseCellClassName) }),
        onHeaderCell: () => ({ className: cx(baseHeaderCellClassName) }),
        render(value: string) {
          if (!value) {
            return <EmptyCellValue />
          }

          const date = parseISO(value)

          return <span title={format(date, 'EEEE')}>{date.toLocaleDateString()}</span>
        }
      },
      {
        title({ sortColumns }) {
          return (
            <CellTitle id='group_count' sortColumns={sortColumns}>
              Groups count
            </CellTitle>
          )
        },
        key: 'group_count',
        dataIndex: 'group_count',
        sorter: true,
        showSorterTooltip: false,
        onCell: () => ({ className: cx(baseCellClassName) }),
        onHeaderCell: () => ({ className: cx(baseHeaderCellClassName) })
      },
      {
        title({ sortColumns }) {
          return (
            <CellTitle id='invoice_sum' sortColumns={sortColumns}>
              Total amount
            </CellTitle>
          )
        },
        key: 'invoice_sum',
        dataIndex: 'invoice_sum',
        sorter: true,
        showSorterTooltip: false,
        onCell: () => ({ className: cx(baseCellClassName) }),
        onHeaderCell: () => ({ className: cx(baseHeaderCellClassName) }),
        render(value: number | null | undefined) {
          if (typeof value !== 'number') {
            return <EmptyCellValue />
          }

          return <span>{currency(value)}</span>
        }
      },
      {
        title: 'Status',
        key: 'status',
        dataIndex: 'status',
        sorter: true,
        showSorterTooltip: false,
        onCell: () => ({ className: cx(baseCellClassName) }),
        onHeaderCell: () => ({ className: cx(baseHeaderCellClassName) }),
        render: (value: PremiumAchPullStatus) => (
          <Tag
            color={PremiumAchPullStatusMapper[value].color}
            title={PremiumAchPullStatusMapper[value].name}
            className='font-lato size-xs'
          >
            {PremiumAchPullStatusMapper[value].name}
          </Tag>
        )
      },
      {
        title: 'Created by',
        key: 'created_by',
        dataIndex: 'created_by',
        sorter: true,
        showSorterTooltip: false,
        onCell: () => ({ className: cx(baseCellClassName) }),
        onHeaderCell: () => ({ className: cx(baseHeaderCellClassName) })
      }
    ],
    []
  )

  /* query */

  const query = useQuery<unknown, unknown, QueryData, QueryContextKey>({
    queryKey: [
      'batches',
      // state.sort,
      pagination
      // { ...state.searchParams, query: debouncedQuery }
    ],
    refetchOnMount: true,

    async queryFn(context) {
      const [, pagination] = context.queryKey
      const { pageSize, currentPage } = pagination
      const sp = new URLSearchParams([
        ...(currentPage > 0 ? [['page', currentPage.toString(10)]] : []),
        ['page-size', pageSize.toString(10)]
      ])

      const { data } = await $api.get<{
        total_filtered: QueryData['totalFiltered']
        rows: PremiumAchPull[]
        total_unfiltered: number
        filters: unknown
      }>(`/batches?${sp.toString()}`)

      return {
        groups: data.rows,
        totalFiltered: data.total_filtered,
        totalUnfiltered: data.total_unfiltered,
        filters: data.filters
      }
    },

    keepPreviousData: true
  })

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])

  const onSelectChange = (newSelectedRowKeys: React.Key[]): void => {
    setSelectedRowKeys(newSelectedRowKeys)
  }

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange
  }

  const handleRowClick = useCallback(
    (record: PremiumAchPull) => {
      navigate(`/premium/pull/${record.id}`)
    },
    [navigate]
  )

  const isFetching = query.isFetching

  return (
    <>
      <div className='flex flex-col gap-4 p-6 font-normal rounded-lg font-lato bg-lucent-background text-lucent-text'>
        <div className='flex justify-between items-start'>
          <div className='flex gap-6 items-center'>
            <h1 className='text-2xl'>Premium ACH Pulls</h1>
          </div>
        </div>
        <div className='flex flex-col rounded-lg border'>
          <div className='flex flex-col'>
            <div className='flex flex-row gap-2 items-center p-4'>
              <div className='flex-grow'>
                <Input
                  placeholder='Search pulls'
                  className='font-lato text-sm py-1 bg-gray-50 [&_input]:bg-transparent rounded [&_input]:placeholder:text-gray-400'
                  prefix={<SearchOutlined className='text-gray-500' />}
                  suffix={
                    // (state.searchParams.query.length ?? 0) > 0
                    //   ? (
                    <button
                      type='button'
                      className='flex justify-center items-center p-2 -m-2 border'
                      // onClick={() => {
                      //   dispatch({
                      //     type: 'set-query',
                      //     payload: ''
                      //   })
                      // }}
                    >
                      <CloseOutlined size={16} className='text-gray-500' />
                    </button>
                    // )
                    // : undefined
                  }
                  // value={state.searchParams.query ?? ''}
                  // onChange={event => {
                  //   dispatch({
                  //     type: 'set-query',
                  //     payload: event.target.value
                  //   })
                  // }}
                />
              </div>
              <Space>
                {/* {!isDefaultFilters && (
                  <Button
                    type='default'
                    // onClick={() => {
                    //   dispatch({
                    //     type: 'clear-filters'
                    //   })
                    // }}
                  >
                    <span className='mr-2 -translate-y-[4px]'>
                      <DeleteFilled
                        className='text-lucent-primary'
                      />
                    </span>
                    <span className='font-lato font-bold text-lucent-primary text-[10px] uppercase -translate-y-[1px]'>
                      Clear Filters
                    </span>
                  </Button>
                )} */}

                <Dropdown.Button
                  type='default'
                  // disabled={query.isFetching}
                  // onClick={() => {
                  //   void query.refetch()
                  // }}
                  icon={
                    <CaretDownOutlined className={cx('w-[12px] h-[12px] !translate-y-[2px]')} />
                  }
                  menu={{
                    items: [30, 60, 60 * 5, 60 * 30].map(seconds => ({
                      key: `refresh-${seconds}`,
                      itemIcon() {
                        return null
                      },
                      label: duration(seconds * 1000)
                    }))
                  }}
                >
                  <div className='flex items-center space-x-1.5'>
                    <SyncOutlined
                      className={cx(
                        query.isFetching && 'animate-custom-spin text-lucent-gray',
                        !query.isFetching && 'text-lucent-primary'
                      )}
                    />

                    <span
                      className={cx(
                        'font-lato font-bold text-[10px] uppercase',
                        query.isFetching && 'text-lucent-gray',
                        !query.isFetching && 'text-lucent-primary'
                      )}
                    >
                      <span>Refresh</span>
                    </span>
                  </div>
                </Dropdown.Button>
              </Space>
            </div>
          </div>
          <Table
            size='small'
            rowKey='id'
            dataSource={query.data?.groups ?? []}
            loading={isFetching}
            rowSelection={rowSelection}
            columns={columns}
            rowClassName='cursor-pointer'
            onRow={record => ({
              onClick: () => {
                handleRowClick(record)
              }
            })}
            pagination={{
              current: pagination.currentPage + 1,
              pageSize: pagination.pageSize,
              pageSizeOptions: ['10', '25', '50', '100'],
              showSizeChanger: true,
              total: query.data?.totalUnfiltered ?? 0,
              showTotal(total, [from, to]) {
                const parts = [
                  `Showing ${from}–${to} of ${total} ${total === 1 ? 'item' : 'items'}`
                ]

                if (
                  typeof query.data?.totalUnfiltered === 'number' &&
                  query.data.totalUnfiltered !== total
                ) {
                  parts.push(
                    `Total ${query.data.totalUnfiltered} ${query.data.totalUnfiltered === 1 ? 'item' : 'items'}`
                  )
                }

                return <span className='text-sm text-gray-500 font-lato'>{parts.join(' • ')}</span>
              },
              hideOnSinglePage: false
            }}
            onChange={(pag, _, __, extra) => {
              if (extra.action === 'paginate') {
                setPagination({
                  pageSize: pag.pageSize ?? 10,
                  currentPage: pag.current === undefined || pag.current === 0 ? 0 : pag.current - 1
                })
              }
            }}
          />
        </div>
      </div>
    </>
  )
}
